import React, { useState, useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectIsOnline,
  selectSyncQueueCount,
  selectPendingFileCount,
  selectPendingFileSizeMb,
  selectIsSyncing,
  selectSyncProgress
} from 'ducks/offline/selectors'
import { triggerSyncRoutine } from 'ducks/offline/actions'
import { getStorageEstimate } from 'services/offlineSync'
import SyncStatusPanel from './SyncStatusPanel'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const OfflineBanner = () => {
  const dispatch = useDispatch()
  const isOnline = useSelector(selectIsOnline)
  const syncQueueCount = useSelector(selectSyncQueueCount)
  const pendingFileCount = useSelector(selectPendingFileCount)
  const pendingFileSizeMb = useSelector(selectPendingFileSizeMb)
  const isSyncing = useSelector(selectIsSyncing)
  const syncProgress = useSelector(selectSyncProgress)
  const [panelOpen, setPanelOpen] = useState(false)
  const [storageInfo, setStorageInfo] = useState(null)

  const pendingCount = syncQueueCount + pendingFileCount

  useEffect(() => {
    if (isOnline || pendingFileCount === 0) {
      setStorageInfo(null)
      return
    }
    const check = async () => {
      const info = await getStorageEstimate()
      setStorageInfo(info)
    }
    check()
    const interval = setInterval(check, 30000)
    return () => clearInterval(interval)
  }, [isOnline, pendingFileCount])

  const isStorageLow = storageInfo && storageInfo.percentUsed >= 80

  if (isOnline && pendingCount === 0 && !isSyncing) return null

  const parts = []
  if (syncQueueCount > 0) parts.push(`${syncQueueCount} change${syncQueueCount !== 1 ? 's' : ''}`)
  if (pendingFileCount > 0) parts.push(`${pendingFileCount} photo${pendingFileCount !== 1 ? 's' : ''} (${pendingFileSizeMb}MB)`)

  const handleClick = () => {
    if (isOnline && pendingCount > 0 && !isSyncing) {
      dispatch(triggerSyncRoutine())
    }
    setPanelOpen(true)
  }

  const getSyncingText = () => {
    if (syncProgress) {
      return `Syncing ${syncProgress.current}/${syncProgress.total}...`
    }
    return 'Syncing...'
  }

  let icon, text, variant

  if (!isOnline) {
    icon = <CloudOffIcon style={{ fontSize: 18 }} />
    text = pendingCount > 0
      ? `Offline — ${parts.join(', ')} pending`
      : 'Offline — you can keep working'
    variant = 'offline'
  } else if (isSyncing) {
    icon = <SpinningIcon><CloudSyncOutlinedIcon style={{ fontSize: 18 }} /></SpinningIcon>
    text = getSyncingText()
    variant = 'syncing'
  } else if (pendingCount > 0) {
    icon = <CloudDoneOutlinedIcon style={{ fontSize: 18 }} />
    text = `${parts.join(', ')} ready to sync`
    variant = 'pending'
  }

  return (
    <>
      <Capsule $variant={variant} onClick={handleClick}>
        {icon}
        <CapsuleText>{text}</CapsuleText>
        {isStorageLow && <WarningAmberIcon style={{ fontSize: 16, opacity: 0.8 }} />}
      </Capsule>
      <SyncStatusPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </>
  )
}

const slideUp = keyframes`
  from { transform: translateX(-50%) translateY(20px); opacity: 0; }
  to { transform: translateX(-50%) translateY(0); opacity: 1; }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinningIcon = styled.span`
  display: flex;
  align-items: center;
  animation: ${spin} 1.5s linear infinite;
`

const Capsule = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.3s ease-out;
  white-space: nowrap;
  user-select: none;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateX(-50%) scale(1.03);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateX(-50%) scale(0.97);
  }

  ${({ $variant }) => $variant === 'offline' && css`
    background: #f59e0b;
    color: #fff;
  `}

  ${({ $variant }) => $variant === 'syncing' && css`
    background: #33becc;
    color: #fff;
  `}

  ${({ $variant }) => $variant === 'pending' && css`
    background: #4caf50;
    color: #fff;
  `}

  @media (max-width: 768px) {
    bottom: calc(var(--bp-float-1) + var(--bp-stack-height, 0px));
    font-size: 12px;
    padding: 8px 16px;
  }
`

const CapsuleText = styled.span`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default OfflineBanner
